import { useNavigate } from "@tanstack/react-router";
import { IconPlus } from "@tabler/icons-react";
import { createElement } from "react";
import type { ReactNode } from "react";

export interface ActionButtonConfig {
  route: string;
  label: string;
  icon: ReactNode;
}

export interface MobileActionButtonHook {
  actionButton: ActionButtonConfig | null;
  handleActionClick: () => void;
}

// Route-based action button configuration
const actionButtonConfigs: Record<string, ActionButtonConfig> = {
  "/tasks": {
    route: "/tasks",
    label: "Create Task",
    icon: createElement(IconPlus, { size: 24 }),
  },
  "/assets": {
    route: "/assets",
    label: "Add Asset",
    icon: createElement(IconPlus, { size: 24 }),
  },
  "/projects": {
    route: "/projects",
    label: "New Project",
    icon: createElement(IconPlus, { size: 24 }),
  },
} as const;

export function useMobileActionButton(
  currentPath: string
): MobileActionButtonHook {
  const navigate = useNavigate();

  // Normalize path (remove trailing slash, handle query params)
  const normalizedPath = currentPath.split("?")[0].replace(/\/$/, "") || "/";
  
  // Find matching action button for current route
  const actionButton = actionButtonConfigs[normalizedPath];

  const handleActionClick = () => {
    // Navigate to appropriate creation route based on current path
    switch (normalizedPath) {
      case "/tasks":
        navigate({ to: "/tasks/new", search: { assetId: undefined, projectId: undefined } });
        break;
      case "/assets":
        navigate({ to: "/assets/new", search: { parentId: undefined } });
        break;
      case "/projects":
        navigate({ to: "/projects/new", search: { assetId: undefined, parentProjectId: undefined } });
        break;
      default:
        // No action for unknown routes
        break;
    }
  };

  return {
    actionButton,
    handleActionClick,
  };
}
